import React, { useState } from 'react';
import { Alert, Button, Col, Row } from 'react-bootstrap';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import FormField from '../../form/FormField';
import * as Yup from 'yup';
import {
  passwordMaximumLimit,
  passwordMinimumLimit2024,
  passwordMinimumLimitDevelopment,
} from '../../common/validation/helper';
import { userApi } from '../../apis';
import { FormikState } from 'formik/dist/types';
import { parseErrorMessage } from '../../common/errors/helpers';
import { isDevelopment } from '../../tracking/environment';

export interface ChangePasswordFormValues {
  currentPassword: string;
  password: string;
  confirmPassword: string;
}

export const PasswordSettings = (): React.JSX.Element => {
  const [passwordChanged, setPasswordChanged] = useState<boolean>(false);

  const misPasswordLength = isDevelopment() ? passwordMinimumLimitDevelopment.value : passwordMinimumLimit2024.value;
  const misPasswordMessage = isDevelopment()
    ? passwordMinimumLimitDevelopment.message
    : passwordMinimumLimit2024.message;
  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required('Required'),
    password: Yup.string()
      .required('Required')
      .min(misPasswordLength, misPasswordMessage)
      .max(passwordMaximumLimit.value, passwordMaximumLimit.message),
    confirmPassword: Yup.string()
      .required('Required')
      .oneOf([Yup.ref('password'), ''], 'Passwords must match'),
  });
  const initialValues: ChangePasswordFormValues = {
    currentPassword: '',
    password: '',
    confirmPassword: '',
  };

  const initialFormikState: Partial<FormikState<ChangePasswordFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const submit = async (values: any, { setStatus, setSubmitting, resetForm }: FormikHelpers<any>) => {
    setSubmitting(true);
    try {
      await userApi.changePassword({
        changePasswordData: {
          currentPassword: values.currentPassword,
          password: values.password,
        },
      });
      resetForm(initialFormikState);
      setPasswordChanged(true);
      setStatus('Password is changed');
    } catch (e: any) {
      setStatus(await parseErrorMessage(e));
      setPasswordChanged(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={submit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, status }: FormikProps<any>) => {
        return (
          <Form>
            <Row>
              <Col md={6}>
                <FormField name='currentPassword' label='Current password' type='password' />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormField name='password' label='New password' type='password' />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormField name='confirmPassword' label='Confirm password' type='password' />
              </Col>
            </Row>
            <Row>
              <Col md={6}>{status && <Alert variant={passwordChanged ? 'success' : 'danger'}>{status}</Alert>}</Col>
            </Row>
            <Row>
              <Col md={4}>
                <Button type='submit' variant='primary' disabled={isSubmitting}>
                  Change password
                </Button>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PasswordSettings;
